import { useMutation } from '@apollo/client';
import Button from "components/Button/Button";
import { InfoIcon } from 'components/Icons/Info';
import AfterPayInfo from 'components/Others/AfterPayInfo';
import ZipPayInfo from 'components/Others/ZipPayInfo';
import Spinner from 'components/Spinner/Spinner';
import { CompositeFramePartsVariationType } from 'constants/dataTypes';
import { frameSizeConst } from 'constants/frameSize';
import { getFrameProductId } from 'constants/woocommerceParentCateoryIds';
import { getLocalCurrency } from 'functions/currency';
import { getArmVariation, getFrameVariation } from 'functions/getVariations';
import { stringToSlug } from 'functions/slug';
import { CREATE_CART } from 'gqlStrings/mutations/addToCart';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from 'redux/features/bag/bagSlice';
import { addToCart } from 'redux/features/cart/cartSlice';
import { RootState } from 'redux/store';
import { showWarningMessage } from 'utils/toast.util';
import { useGlassesStore } from 'zus/glassesStore';
import * as ga from '../../lib/ga';
import { sendAddToCartEvent } from '../../lib/tracking';
import { getSession } from 'functions/session';
import { wcApiDataService } from 'functions/wcApi';
import { parseAsString } from 'nuqs';
import { useQueryState } from 'nuqs';


export default function FrameAddToCartComponent({ frameColor, checkVariationOnColorPicker, price }: { frameColor: string; checkVariationOnColorPicker?: Function; price: number }) {
    const isLoad = useSelector((state: RootState) => state.bagEvent.loading);
    const dispatch = useDispatch();

    const [frameShape, setFrameShapeQuery] = useQueryState('shape', parseAsString.withDefault('classic'));
    const [armColorQuery, setArmColorQuery] = useQueryState('armColor');
    const armColor = armColorQuery ? armColorQuery : frameColor;
    //Zustand states
    const updateData = useGlassesStore((state) => state.updateData);


    const frameSizeZ = useGlassesStore((state) => state.frame_size)
    const armsSizeZ = useGlassesStore((state) => state.arms_size)


    const getRegularSize = (size: string, isFrame: boolean) => {
        let sizeTitle: string | undefined;
        frameSizeConst.forEach((frameSize: any) => {
            if (frameSize.short === size) {
                sizeTitle = frameSize.slug;
            }
        });

        if (isFrame && frameShape.toLowerCase() === 'round') {
            switch (sizeTitle!) {
                case 'small':
                    return 'rounds';
                case 'medium':
                    return 'roundm';
                case 'large':
                    return 'roundl';
                default:
                    return sizeTitle; // This will handle 'extra-small' or any other sizes
            }
        }

        return stringToSlug(sizeTitle);
    }
    const router = useRouter();

    const getFrameShapeSize = (size: string) => {
        if (frameShape.toLowerCase() === 'round') {
            return 'round' + size.toLowerCase();
        }
        return size;
    }

    const addToCartData = async () => {
        const productDetailPageData: any = await wcApiDataService('glasses/frame-set', '', router.locale);

        const glassesProduct = productDetailPageData?.components;

        const frame_products = glassesProduct.Frame;
        const arm_products = glassesProduct.Arms;

        const composite_id = getFrameProductId(router.locale);

        const arm_databaseId: string = arm_products.component_id;
        const frame_databaseId: string = frame_products.component_id;

        const arm_variation: CompositeFramePartsVariationType | undefined = getArmVariation(arm_products!, armColor, armsSizeZ);
        const frame_variation: CompositeFramePartsVariationType | undefined = getFrameVariation(frame_products!, frameColor, getFrameShapeSize(frameSizeZ));

        if (arm_variation == undefined || frame_variation == undefined) {
            showWarningMessage('This variation is not available', false);
            return;
        }

        const extra_data = {
            [arm_databaseId]: {
                product_id: arm_products?.product.product_id,
                quantity: 1,
                optional_selected: 'yes',
                variation_id: arm_variation.variation_id,
                region_product_id: arm_variation.meta.region_product_ids,
                attributes: {
                    attribute_pa_color: armColor,
                    attribute_pa_size: getRegularSize(armsSizeZ, false) // Pass false for arms
                },
                quantity_min: 1,
                quantity_max: 1,
                discount: '',
                optional: 'no',
                static: 'yes',
                title: 'Arms',
                composite_id,
                type: 'variable'
            },
            [frame_databaseId]: {
                product_id: frame_products?.product.product_id,
                quantity: 1,
                optional_selected: 'yes',
                variation_id: frame_variation.variation_id,
                region_product_id: frame_variation.meta.region_product_ids,
                attributes: {
                    attribute_pa_color: frameColor,
                    attribute_pa_size: getRegularSize(frameSizeZ, true) // Pass true for frame
                },
                quantity_min: 1,
                quantity_max: 1,
                discount: '',
                optional: 'no',
                static: 'yes',
                title: 'Frame',
                composite_id,
                type: 'variable'
            }
        };

        console.log('frame addToCartData extra_data', extra_data);

        var cartData;
        if (updateData) {
            cartData = {
                variables: {
                    input: {
                        removeItem: updateData.key,
                        productId: composite_id,
                        clientMutationId: getSession(),
                        quantity: updateData.quantity,
                        extraData: JSON.stringify(extra_data),
                    }
                }
            };
        } else {
            cartData = {
                variables: {
                    input: {
                        clientMutationId: getSession(),
                        extraData: JSON.stringify(extra_data),
                        productId: composite_id,
                        quantity: 1,
                    }
                }
            };
        }

        dataLayer(); // Move this call here, after all the data is prepared

        return cartData;
    }

    const dataLayer = () => {
        ga.addDataLayer({
            event: 'addToCart',
            ecommerce: {
                currencyCode: getLocalCurrency(router.locale),
                add: {
                    products: [
                        {
                            name: 'Frame' + ' - ' + frameColor,
                            variant: frameColor,
                            price: price,
                            category: 'Frames',
                            quantity: 1
                        }
                    ]
                }
            }
        });
        
        // Send event to Meta Conversion API
        sendAddToCartEvent({
            product_id: `frames-${frameColor}`,
            product_name: `${frameColor} Frames`,
            value: Number(price),
            currency: getLocalCurrency(router.locale),
            content_category: 'Frames',
            contents: [
                {
                    id: `frames-${frameColor}`,
                    quantity: 1,
                    item_price: Number(price)
                }
            ]
        });
    }

    const [mutateCreateCart] = useMutation(CREATE_CART, {
        context: { clientName: 'laravel' }
    });

    const addToCartClick = async () => {
        const session = getSession();
        if (!session) {
            mutateCreateCart({
                variables: {
                    input: {
                        isEmpty: true
                    }
                }
            }).then(() => {
                setTimeout(() => {
                    addToCartClick();
                }, 250);
            });
            return;
        }
        dispatch(setLoading(true));

        try {
            const cartData = await addToCartData();
            dispatch(addToCart(cartData));
        } catch (error) {
            console.error("Error adding to cart:", error);
            // Handle the error appropriately, e.g., show an error message to the user
        } finally {
            dispatch(setLoading(false));
        }
    }


    const getHashSlugFromColorSize = (size: string, color: string) => {
        const slug = `${color}`.toLowerCase().split(' ').join('-') + size;
        return slug;
    };


    const isSelectedVariationAvailable = useMemo(() => {

        if (checkVariationOnColorPicker) {
            const frame_check = checkVariationOnColorPicker(armColorQuery ? 'Frame' : 'both', frameSizeZ, frameColor);
            const arms_check = checkVariationOnColorPicker('Arms', armsSizeZ, armColor);
            if (frame_check && arms_check) {
                return true;
            }
        }
        return false;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [frameSizeZ, frameColor, armsSizeZ, armColor]);

    if (!isSelectedVariationAvailable) {
        return (<div className="flex max-w-[308px] mt-[24px] bg-[#fef4cb] items-center gap-[8px] px-[16px] py-[17px]">
            <InfoIcon />
            <p className="text-[14px] leading-[15px] text-[#616161]">
                This product is not available right now
            </p>
        </div>)
    } else {
        return (
            <>
                <Button
                    onClick={addToCartClick}
                    disabled={!price || price <= 0}
                    className="mt-4 w-full justify-center bg-brand-black50 !px-6 text-center mdx:sticky mdx:bottom-3"
                    textClass="font-medium text-white text-sm">
                    <span className="flex items-center">
                        {isLoad && (
                            <span className="mr-2 mt-[1px]">
                                <Spinner fill="white" />
                            </span>
                        )}{' '}
                        <span>
                            {updateData ? 'Update cart' : 'Add to cart'}
                            {(price && price > 0) ? (` $${price}`) : ''}
                        </span>
                    </span>
                </Button>
                <AfterPayInfo total={price} />
                <ZipPayInfo total={price} />
            </>
        );
    }
}
